/**
 * useCredits — React hooks for wallet balance + top-up products.
 *
 * A15-N6: Wallet API + Frontend Components.
 * Uses apiClient with unwrap pattern (res?.data ?? res).
 */

import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../lib/apiClient';

/**
 * useCredits — wallet balance, lifetime stats, recent transactions.
 */
export function useCredits() {
  const [balance, setBalance] = useState(null);
  const [balanceEur, setBalanceEur] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);

  const fetchWallet = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/api/v1/aif/credits/wallet');
      const data = res?.data ?? res;
      setBalance(data.balance_credits);
      setBalanceEur(data.balance_eur);
      setRecentTransactions(data.recent_transactions || []);
    } catch (err) {
      setError(err.message || 'Failed to load wallet');
      setBalance(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWallet();
  }, [fetchWallet]);

  return {
    balance,
    balanceEur,
    loading,
    error,
    recentTransactions,
    refetch: fetchWallet,
  };
}

/**
 * useTopUpProducts — available credit packages.
 */
export function useTopUpProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function loadProducts() {
      try {
        const res = await apiClient.get('/api/v1/aif/credits/products');
        const data = res?.data ?? res;
        setProducts(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message || 'Failed to load packages');
      } finally {
        setLoading(false);
      }
    }
    loadProducts();
  }, []);

  return { products, loading, error };
}
